// STYL — pop-up newsletter
// Ouverture différée ou à l'intention de sortie, mémorisée en localStorage,
// inscription via le formulaire client natif Shopify (envoi en arrière-plan avec repli natif).
(() => {
  if (customElements.get('styl-popup')) return;

  const STORAGE_DISMISSED = 'styl-popup:dismissed-at';
  const STORAGE_SUBSCRIBED = 'styl-popup:subscribed';
  const DAY = 24 * 60 * 60 * 1000;

  const storage = {
    get(key) {
      try {
        return window.localStorage.getItem(key);
      } catch {
        return null;
      }
    },
    set(key, value) {
      try {
        window.localStorage.setItem(key, value);
      } catch {
        // stockage indisponible (navigation privée…) : on ignore
      }
    },
  };

  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class StylPopup extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.form = this.closest('form');
      if (!this.dialog || !this.form) return;

      this.abort = new AbortController();
      const { signal } = this.abort;
      this.designMode = this.hasAttribute('data-design-mode');

      this.querySelectorAll('[data-styl-popup-close]').forEach((button) =>
        button.addEventListener('click', () => this.close(), { signal })
      );

      // Clic sur le fond : la cible est le <dialog> lui-même, hors du panneau
      this.dialog.addEventListener(
        'click',
        (event) => {
          if (event.target === this.dialog) this.close();
        },
        { signal }
      );

      // Échap : on remplace la fermeture native pour garder l'animation et la mémorisation
      this.dialog.addEventListener(
        'cancel',
        (event) => {
          event.preventDefault();
          this.close();
        },
        { signal }
      );

      this.form.addEventListener('submit', (event) => this.onSubmit(event), { signal });
      this.querySelector('[data-styl-popup-copy]')?.addEventListener('click', () => this.copyCode(), { signal });

      // Retour d'un envoi natif (repli) : on rouvre directement sur le bon état.
      // Shopify ajoute l'id du formulaire envoyé en ancre, ce qui évite de réagir
      // à un autre formulaire client de la page (ex. newsletter du pied de page).
      const state = window.location.hash === `#${this.form.id}` ? this.dataset.state : 'idle';
      this.dataset.state = state;
      if (state === 'success') {
        storage.set(STORAGE_SUBSCRIBED, '1');
        this.open();
        return;
      }
      if (state === 'error') {
        this.open();
        return;
      }

      if (this.designMode) return;
      if (this.dataset.enabled !== 'true' || !this.shouldShow()) return;
      this.schedule(signal);
    }

    disconnectedCallback() {
      this.abort?.abort();
      window.clearTimeout(this.timer);
      this.unlockScroll();
    }

    shouldShow() {
      if (storage.get(STORAGE_SUBSCRIBED)) return false;
      const dismissedAt = Number(storage.get(STORAGE_DISMISSED));
      const days = Number(this.dataset.dismissDays) || 30;
      if (dismissedAt && Date.now() - dismissedAt < days * DAY) return false;
      return true;
    }

    schedule(signal) {
      // Le pop-up va s'afficher : on charge l'image dès maintenant pour qu'elle soit prête à l'ouverture
      this.querySelector('.styl-popup__media img')?.setAttribute('loading', 'eager');

      const delay = Math.max(0, Number(this.dataset.delay) || 0) * 1000;
      this.timer = window.setTimeout(() => this.openWhenReady(signal), delay);

      const canExitIntent =
        this.dataset.exitIntent === 'true' && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      if (!canExitIntent) return;

      document.documentElement.addEventListener(
        'mouseleave',
        (event) => {
          if (event.clientY <= 0) this.openWhenReady(signal);
        },
        { signal }
      );
    }

    // La bannière de consentement cookies de Shopify passe en priorité :
    // le pop-up (modal) la rendrait inaccessible, on attend donc la réponse du visiteur.
    openWhenReady(signal) {
      if (!this.consentBannerVisible()) {
        this.open();
        return;
      }
      if (this.waitingForConsent) return;
      this.waitingForConsent = true;
      document.addEventListener(
        'visitorConsentCollected',
        () => {
          this.timer = window.setTimeout(() => this.open(), 1500);
        },
        { once: true, signal }
      );
    }

    consentBannerVisible() {
      const banner = document.getElementById('shopify-pc__banner');
      if (!banner) return false;
      const style = window.getComputedStyle(banner);
      return style.display !== 'none' && style.visibility !== 'hidden' && banner.getClientRects().length > 0;
    }

    open() {
      if (this.dialog.open || this.hasOpened) return;
      this.hasOpened = !this.designMode;
      window.clearTimeout(this.timer);

      this.previousFocus = document.activeElement;
      this.lockScroll();
      this.dialog.showModal();
      // Laisse le navigateur peindre l'état initial avant d'animer
      requestAnimationFrame(() => requestAnimationFrame(() => this.dialog.classList.add('is-open')));

      if (this.dataset.state === 'success') {
        this.querySelector('[data-styl-popup-success-title]')?.focus();
      } else if (this.dataset.state === 'error') {
        this.form.querySelector('input[type="email"]')?.focus();
      }
    }

    close({ remember = true } = {}) {
      if (!this.dialog.open) return;
      if (remember && !this.designMode && !storage.get(STORAGE_SUBSCRIBED)) {
        storage.set(STORAGE_DISMISSED, String(Date.now()));
      }

      this.dialog.classList.remove('is-open');
      const finish = () => {
        this.dialog.close();
        this.unlockScroll();
        if (this.previousFocus instanceof HTMLElement) this.previousFocus.focus({ preventScroll: true });
      };

      if (reduceMotion()) {
        finish();
      } else {
        window.setTimeout(finish, 450);
      }
    }

    lockScroll() {
      document.documentElement.style.overflow = 'hidden';
    }

    unlockScroll() {
      document.documentElement.style.removeProperty('overflow');
    }

    async onSubmit(event) {
      const email = this.form.querySelector('input[type="email"]');
      if (!email || !email.checkValidity()) return; // validation native du navigateur

      event.preventDefault();
      const button = this.form.querySelector('button[type="submit"]');
      const message = this.querySelector('[data-styl-popup-message]');
      button?.setAttribute('aria-busy', 'true');
      if (message) message.textContent = '';

      try {
        const response = await fetch(this.form.action, {
          method: 'POST',
          body: new FormData(this.form),
          headers: { Accept: 'text/html' },
        });
        const url = new URL(response.url);

        // Protection anti-robots de Shopify : on laisse le formulaire natif gérer le défi
        if (url.pathname.includes('/challenge')) {
          HTMLFormElement.prototype.submit.call(this.form);
          return;
        }

        if (url.searchParams.get('customer_posted') === 'true') {
          this.showSuccess();
          return;
        }

        // Réponse rendue avec des erreurs : on récupère l'état calculé par Liquid
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const remote = doc.querySelector(`styl-popup[data-section-id="${this.dataset.sectionId}"]`);
        if (remote?.dataset.state === 'success') {
          this.showSuccess();
          return;
        }
        this.showError(message, email);
      } catch {
        this.showError(message, email);
      } finally {
        button?.removeAttribute('aria-busy');
      }
    }

    showError(message, email) {
      if (message) message.textContent = this.dataset.errorMessage || '';
      email?.setAttribute('aria-invalid', 'true');
      email?.focus();
    }

    showSuccess() {
      storage.set(STORAGE_SUBSCRIBED, '1');
      this.dataset.state = 'success';
      this.querySelector('[data-styl-popup-view="form"]')?.setAttribute('hidden', '');
      this.querySelector('[data-styl-popup-view="success"]')?.removeAttribute('hidden');
      this.querySelector('[data-styl-popup-success-title]')?.focus();
    }

    async copyCode() {
      const code = this.querySelector('[data-styl-popup-code]')?.textContent.trim();
      const status = this.querySelector('[data-styl-popup-copy-status]');
      if (!code) return;

      try {
        await navigator.clipboard.writeText(code);
      } catch {
        // Repli : sélection du code pour copie manuelle
        const range = document.createRange();
        range.selectNodeContents(this.querySelector('[data-styl-popup-code]'));
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
        document.execCommand?.('copy');
      }
      if (status) status.textContent = this.dataset.copiedLabel || '';
    }
  }

  customElements.define('styl-popup', StylPopup);

  // Éditeur de thème : affiche le pop-up quand sa section est sélectionnée
  const findPopup = (event) =>
    document.querySelector(`styl-popup[data-section-id="${event.detail?.sectionId}"]`);

  document.addEventListener('shopify:section:select', (event) => findPopup(event)?.open());
  document.addEventListener('shopify:section:deselect', (event) => findPopup(event)?.close({ remember: false }));
})();
